import type { ChangeEvent } from "react";
import { useState } from "react";
import { Input, InputAdornment, makeStyles, useTheme } from "@material-ui/core";

import { Close, Filter, Search as SearchIcon } from "@eroad/icons/svg";

import type { CoretexTheme } from "../theme";
import IconButton from "./IconButton";

const useStyles = makeStyles((theme: CoretexTheme) => ({
  root: {
    border: `1px solid ${theme.palette.mono.gray}`,
    height: "44px",
    padding: theme.spacing(2),
    width: "100%",
    "&.Mui-focused": {
      border: `1px solid ${theme.palette.colour.primaryBlue}`,
      "& > $search path": {
        fill: theme.palette.colour.primaryBlue,
      },
    },
  },
  input: {
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.mono.ultraDark,
    "&::placeholder": {
      opacity: "1 !important",
      fontStyle: "italic",
    },
    "&:focus": {
      "&::placeholder": {
        opacity: "0 !important",
      },
    },
  },
  search: {
    margin: theme.spacing(2),
  },
}));

interface SearchProps {
  placeholder: string;
  selected: boolean;
  onToggle: (selected: boolean) => void;
  onChange?: (text: string) => void;
}

const Search = ({ placeholder, selected, onToggle, onChange }: SearchProps): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme<CoretexTheme>();

  const [text, setText] = useState("");

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }

    setText(e.target.value);
  };

  const handleClear = () => {
    if (onChange) {
      onChange("");
    }

    setText("");
  };

  const filters = (
    <InputAdornment position="end">
      {text && (
        <IconButton data-testid="search-field-clear-button" onClick={handleClear} selected={false} size="small">
          <Close width={8} height={8} />
        </IconButton>
      )}
      <IconButton data-testid="search-field-filter-button" onClick={() => onToggle(!selected)} selected={selected}>
        <Filter />
      </IconButton>
    </InputAdornment>
  );

  const search = (
    <InputAdornment data-testid="search-field-icon" position="start" className={classes.search}>
      <SearchIcon width={16} height={16} fill={theme.palette.mono.ultraDark} />
    </InputAdornment>
  );

  return (
    <Input
      data-testid="search-field"
      placeholder={placeholder}
      endAdornment={filters}
      startAdornment={search}
      disableUnderline
      value={text}
      classes={{
        input: classes.input,
        root: classes.root,
      }}
      onChange={handleTextChange}
    />
  );
};

export default Search;
